import { useState, useCallback } from "react";
import { useMount } from "@/hooks/useLifeCycles";

const GRID_ROW_COUNT = 14;
const GRID_COL_COUNT = 25;

export type ViewerGridMask = Omit<MotionDetectionZone["gridMask"], "bits"> & {
  orientation: "portrait" | "landscape";
};

export const useDetectionZones = ({
  cameraSettings,
  getDetectionZonesPreview,
  initialState
}: {
  cameraSettings: CSTypes.AllCameraSettings;
  getDetectionZonesPreview?: CSTypes.CameraSettingsStepProps["getDetectionZonesPreview"];
  initialState?: {
    viewerGridMask: ViewerGridMask | null;
    cameraGridMask: MotionDetectionZone["gridMask"] | null;
    preview: DetectionZonesPreview | null;
  };
}) => {
  const [preview, setPreview] = useState<DetectionZonesPreview | null>(null);
  const [cameraGridMask, setCameraGridMask] = useState<MotionDetectionZone["gridMask"] | null>(null);
  const [viewerGridMask, setViewerGridMask] = useState<ViewerGridMask | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);

  const calculateViewerGridMask = useCallback(
    (cameraGridMask: MotionDetectionZone["gridMask"], previewOrientation: ViewerGridMask["orientation"]) => {
      const { rows: cameraRows, cols: cameraCols } = cameraGridMask;
      const isPreviewPortrait = previewOrientation === "portrait";
      return {
        rows: isPreviewPortrait ? cameraCols : cameraRows,
        cols: isPreviewPortrait ? cameraRows : cameraCols,
        orientation: previewOrientation
      };
    },
    []
  );

  const getPreview = useCallback(async () => {
    if (!getDetectionZonesPreview) return;
    try {
      setIsLoading(true);
      setError(false);
      log.detectionZones("About to get camera preview");
      const res = await getDetectionZonesPreview();
      if (!res) {
        setError(true);
        return;
      }
      const { imageWidth, imageHeight } = res.preview;

      log.detectionZones("Received camera preview", res.preview);
      setPreview(res.preview);
      let cameraGridMask = cameraSettings?.settings?.motionDetectionZone?.gridMask;
      if (cameraGridMask) setCameraGridMask(cameraGridMask);
      else {
        cameraGridMask = {
          bits: [],
          rows: GRID_ROW_COUNT,
          cols: GRID_COL_COUNT
        };
        setCameraGridMask(cameraGridMask);
      }
      const viewerGridMask = calculateViewerGridMask(
        cameraGridMask,
        imageWidth > imageHeight ? "landscape" : "portrait"
      );
      setViewerGridMask(viewerGridMask);
      log.detectionZones("Viewer grid mask", viewerGridMask);
      log.detectionZones("Camera grid mask", cameraGridMask);
    } catch (err) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [cameraSettings, getDetectionZonesPreview, calculateViewerGridMask]);

  useMount(() => {
    const { viewerGridMask, cameraGridMask, preview } = initialState || {};
    if (viewerGridMask && cameraGridMask && preview) {
      setViewerGridMask(viewerGridMask);
      setCameraGridMask(cameraGridMask);
      setPreview(preview);
      setIsLoading(false);
    } else {
      getPreview();
    }
  });

  const getPreviewSrc = useCallback(() => {
    if (!preview) return null;
    return URL.createObjectURL(new Blob([preview.imageData]));
  }, [preview]);

  return {
    viewerGridMask,
    cameraGridMask,
    preview,
    previewSrc: getPreviewSrc(),
    isLoading,
    error
  };
};
